// Persistent Filter State Management
// Preserves filter selections across data updates and component re-renders

import { useState, useEffect, useCallback } from 'react';
import { MoneynessC } from './moneynessCUtils';
import { isStablePrefetchFilterLock, readStablePrimarySessionFirst } from './stableTabStorage';

export interface ComponentFilterState {
  selectedUnderlying: string;
  selectedExpiry: string;
  /** Multiple expiries (e.g. Detailed Options Charts). When absent, derive from `selectedExpiry`. */
  selectedExpiries?: string[];
  selectedSide: string;
  selectedMoneyness: string;
  selectedMoneynessC: MoneynessC | 'all'; // New MoneynessC filter
  selectedDataMode: 'accumulated' | '5min';
  // Additional filters for specific components
  selectedSymbol?: string; // For components using symbol instead of underlying
  selectedStrategy?: string; // For strategy-based components
  selectedTimeframe?: string; // For time-based analysis
  selectedVolume?: string; // For volume-based filters
  selectedDelta?: string; // For delta-based filters
}

const DEFAULT_FILTER_STATE: ComponentFilterState = {
  selectedUnderlying: 'all',
  selectedExpiry: 'all',
  selectedExpiries: ['all'],
  selectedSide: 'all',
  selectedMoneyness: 'all',
  selectedMoneynessC: 'all',
  selectedDataMode: 'accumulated',
  selectedSymbol: 'all',
  selectedStrategy: 'all',
  selectedTimeframe: 'all',
  selectedVolume: 'all',
  selectedDelta: 'all'
};

// Storage key generator for component-specific filter states
const getStorageKey = (componentName: string) => `filter-state-${componentName}`;

// Stable prefetch lock: force snapshot chain + accumulated mode; underlying follows Fetch bar only.

function applyStablePersistentRules(merged: ComponentFilterState): ComponentFilterState {
  if (typeof localStorage === 'undefined') return merged;
  const dataSource = localStorage.getItem('mmec_data_source') || 'rtl_legacy';
  if (dataSource !== 'stable' || !isStablePrefetchFilterLock()) return merged;
  const u = readStablePrimarySessionFirst();
  let out: ComponentFilterState = { ...merged, selectedDataMode: 'accumulated' };
  if (u && u.trim() !== '') {
    out = { ...out, selectedUnderlying: u.trim() };
  }
  return out;
}

// Load filter state from localStorage with fallback handling.
const loadFilterState = (componentName: string): ComponentFilterState => {
  let merged: ComponentFilterState = { ...DEFAULT_FILTER_STATE };
  try {
    const stored = localStorage.getItem(getStorageKey(componentName));
    if (stored) {
      const parsed = JSON.parse(stored);
      merged = { ...DEFAULT_FILTER_STATE, ...parsed };
      console.log(`📋 Loaded persistent filters for ${componentName}:`, merged);
      const multiExpiryComponents = new Set([
        'DetailedOptionsCharts',
        'VolumeOISpotGammaChart',
        'VerticalSpotGammaVsOIChart',
        'OptionChangeTracker',
      ]);
      if (
        multiExpiryComponents.has(componentName) &&
        !('selectedExpiries' in parsed) &&
        (!Array.isArray(merged.selectedExpiries) || merged.selectedExpiries.length === 0)
      ) {
        merged.selectedExpiries = merged.selectedExpiry === 'all' ? ['all'] : [merged.selectedExpiry];
      }
    } else {
      console.log(`🆕 Using default filters for ${componentName}`);
    }
    return applyStablePersistentRules(merged);
  } catch (error) {
    console.warn(`⚠️ Failed to load filter state for ${componentName}:`, error);
    return applyStablePersistentRules(merged);
  }
};

// Save filter state to localStorage with error handling
const saveFilterState = (componentName: string, state: ComponentFilterState): void => {
  try {
    localStorage.setItem(getStorageKey(componentName), JSON.stringify(state));
    console.log(`💾 Saved persistent filters for ${componentName}:`, state);
  } catch (error) {
    console.warn(`⚠️ Failed to save filter state for ${componentName}:`, error);
  }
};

// Custom hook for persistent filter state with enhanced functionality
export const usePersistentFilters = (
  componentName: string, 
  initialDataMode: 'accumulated' | '5min' = 'accumulated',
  options: {
    enableSymbol?: boolean;
    enableStrategy?: boolean;
    enableTimeframe?: boolean;
    enableVolume?: boolean;
    enableDelta?: boolean;
    debugMode?: boolean;
  } = {}
) => {
  const { debugMode = false } = options;
  
  // Load initial state from localStorage or use defaults
  const [filterState, setFilterState] = useState<ComponentFilterState>(() => {
    const loaded = loadFilterState(componentName);
    if (isStablePrefetchFilterLock()) {
      return loaded;
    }
    return { ...loaded, selectedDataMode: initialDataMode };
  });

  const coerceStableLockedFilters = useCallback(() => {
    setFilterState((prev) => {
      if (!isStablePrefetchFilterLock()) return prev;
      const u = readStablePrimarySessionFirst().trim();
      const dataOk = prev.selectedDataMode === 'accumulated';
      const undOk = !u || prev.selectedUnderlying === u;
      if (dataOk && undOk) return prev;
      return {
        ...prev,
        selectedDataMode: 'accumulated',
        ...(u ? { selectedUnderlying: u } : {}),
      };
    });
  }, []);

  useEffect(() => {
    coerceStableLockedFilters();
    window.addEventListener('dataSourceChanged', coerceStableLockedFilters);
    window.addEventListener('stableDataFetchRequested', coerceStableLockedFilters);
    window.addEventListener('mmecFuturesModeChanged', coerceStableLockedFilters);
    return () => {
      window.removeEventListener('dataSourceChanged', coerceStableLockedFilters);
      window.removeEventListener('stableDataFetchRequested', coerceStableLockedFilters);
      window.removeEventListener('mmecFuturesModeChanged', coerceStableLockedFilters);
    };
  }, [coerceStableLockedFilters]);

  // Save state to localStorage whenever it changes (but not on initial load)
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (isInitialized) {
      let snapshot = filterState;
      if (isStablePrefetchFilterLock()) {
        const u = readStablePrimarySessionFirst().trim();
        snapshot = {
          ...filterState,
          selectedDataMode: 'accumulated',
          ...(u ? { selectedUnderlying: u } : {}),
        };
      }
      saveFilterState(componentName, snapshot);
      if (debugMode) {
        console.log(`🔄 Filter state updated for ${componentName}:`, snapshot);
      }
    } else {
      setIsInitialized(true);
    }
  }, [componentName, filterState, debugMode, isInitialized]);

  // When user clicks Fetch in Stable bar, preset underlying across all tools (including already-mounted)
  useEffect(() => {
    const handler = () => {
      const dataSource = localStorage.getItem('mmec_data_source') || 'rtl_legacy';
      const underlying = dataSource === 'stable' ? readStablePrimarySessionFirst() : '';
      if (dataSource === 'stable' && underlying && underlying.trim() !== '' && isStablePrefetchFilterLock()) {
        setFilterState(prev => ({ ...prev, selectedUnderlying: underlying.trim() }));
      }
    };
    window.addEventListener('stableDataFetchRequested', handler);
    return () => window.removeEventListener('stableDataFetchRequested', handler);
  }, []);

  // When data source switches to Legacy RTL, reset underlying to 'all' so table shows all data (no stale Stable symbol)
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ source?: string }>;
      if (ev?.detail?.source === 'rtl_legacy') {
        setFilterState(prev => ({ ...prev, selectedUnderlying: 'all' }));
      }
    };
    window.addEventListener('dataSourceChanged', handler);
    return () => window.removeEventListener('dataSourceChanged', handler);
  }, []);

  // Individual setters for each filter with debugging
  const setSelectedUnderlying = useCallback((value: string) => {
    if (isStablePrefetchFilterLock()) {
      if (debugMode) console.log(`🎯 ${componentName}: Ignoring underlying (Stable prefetch lock)`);
      return;
    }
    if (debugMode) console.log(`🎯 ${componentName}: Setting underlying to ${value}`);
    setFilterState(prev => ({ ...prev, selectedUnderlying: value }));
  }, [componentName, debugMode]);

  const setSelectedExpiry = useCallback((value: string) => {
    if (debugMode) console.log(`📅 ${componentName}: Setting expiry to ${value}`);
    setFilterState(prev => ({
      ...prev,
      selectedExpiry: value,
      selectedExpiries: value === 'all' ? ['all'] : [value],
    }));
  }, [componentName, debugMode]);

  const expiryDigitsNorm = (e: string) => String(e ?? '').replace(/\D/g, '').slice(0, 8);

  const setSelectedExpiries = useCallback(
    (next: string[]) => {
      if (debugMode) console.log(`📅 ${componentName}: Setting expiries to`, next);
      setFilterState(prev => {
        let norm =
          !next?.length || next.includes('all')
            ? ['all']
            : Array.from(new Set(next.filter((x) => String(x).trim() !== '')));
        const sortedNonAll = norm
          .filter((x) => x !== 'all')
          .sort((a, b) => expiryDigitsNorm(a).localeCompare(expiryDigitsNorm(b)));
        const finalList = sortedNonAll.length ? sortedNonAll : ['all'];
        const primary = finalList[0] === 'all' ? 'all' : finalList[0];
        return { ...prev, selectedExpiries: finalList, selectedExpiry: primary };
      });
    },
    [componentName, debugMode]
  );

  const setSelectedSide = useCallback((value: string) => {
    if (debugMode) console.log(`📊 ${componentName}: Setting side to ${value}`);
    setFilterState(prev => ({ ...prev, selectedSide: value }));
  }, [componentName, debugMode]);

  const setSelectedMoneyness = useCallback((value: string) => {
    if (debugMode) console.log(`💰 ${componentName}: Setting moneyness to ${value}`);
    setFilterState(prev => ({ ...prev, selectedMoneyness: value }));
  }, [componentName, debugMode]);

  const setSelectedMoneynessC = useCallback((value: MoneynessC | 'all') => {
    if (debugMode) console.log(`💰 ${componentName}: Setting moneynessC to ${value}`);
    setFilterState(prev => ({ ...prev, selectedMoneynessC: value }));
  }, [componentName, debugMode]);

  const setSelectedDataMode = useCallback((value: 'accumulated' | '5min') => {
    if (isStablePrefetchFilterLock()) {
      if (debugMode) console.log(`⏱️ ${componentName}: Ignoring data mode (Stable prefetch lock)`);
      return;
    }
    if (debugMode) console.log(`⏱️ ${componentName}: Setting data mode to ${value}`);
    setFilterState(prev => ({ ...prev, selectedDataMode: value }));
  }, [componentName, debugMode]);

  // Additional setters for extended filters
  const setSelectedSymbol = useCallback((value: string) => {
    if (debugMode) console.log(`🔤 ${componentName}: Setting symbol to ${value}`);
    setFilterState(prev => ({ ...prev, selectedSymbol: value }));
  }, [componentName, debugMode]);

  const setSelectedStrategy = useCallback((value: string) => {
    if (debugMode) console.log(`🎲 ${componentName}: Setting strategy to ${value}`);
    setFilterState(prev => ({ ...prev, selectedStrategy: value }));
  }, [componentName, debugMode]);

  const setSelectedTimeframe = useCallback((value: string) => {
    if (debugMode) console.log(`⏰ ${componentName}: Setting timeframe to ${value}`);
    setFilterState(prev => ({ ...prev, selectedTimeframe: value }));
  }, [componentName, debugMode]);

  const setSelectedVolume = useCallback((value: string) => {
    if (debugMode) console.log(`📈 ${componentName}: Setting volume to ${value}`);
    setFilterState(prev => ({ ...prev, selectedVolume: value }));
  }, [componentName, debugMode]);

  const setSelectedDelta = useCallback((value: string) => {
    if (debugMode) console.log(`📐 ${componentName}: Setting delta to ${value}`);
    setFilterState(prev => ({ ...prev, selectedDelta: value }));
  }, [componentName, debugMode]);

  // Reset all filters to defaults
  const resetFilters = useCallback(() => {
    let resetState: ComponentFilterState = { ...DEFAULT_FILTER_STATE, selectedDataMode: initialDataMode };
    if (isStablePrefetchFilterLock()) {
      const u = readStablePrimarySessionFirst().trim();
      resetState = {
        ...resetState,
        selectedDataMode: 'accumulated',
        ...(u ? { selectedUnderlying: u } : {}),
      };
    }
    if (debugMode) console.log(`🔄 ${componentName}: Resetting filters to defaults`);
    setFilterState(resetState);
  }, [componentName, initialDataMode, debugMode]);

  // Clear stored state (useful for debugging)
  const clearStoredState = useCallback(() => {
    try {
      localStorage.removeItem(getStorageKey(componentName));
      let resetState: ComponentFilterState = { ...DEFAULT_FILTER_STATE, selectedDataMode: initialDataMode };
      if (isStablePrefetchFilterLock()) {
        const u = readStablePrimarySessionFirst().trim();
        resetState = {
          ...resetState,
          selectedDataMode: 'accumulated',
          ...(u ? { selectedUnderlying: u } : {}),
        };
      }
      setFilterState(resetState);
      console.log(`🗑️ Cleared stored state for ${componentName}`);
    } catch (error) {
      console.warn(`⚠️ Failed to clear stored state for ${componentName}:`, error);
    }
  }, [componentName, initialDataMode]);

  return {
    // Current filter values
    selectedUnderlying: filterState.selectedUnderlying,
    selectedExpiry: filterState.selectedExpiry,
    selectedExpiries:
      filterState.selectedExpiries?.length
        ? filterState.selectedExpiries
        : filterState.selectedExpiry === 'all'
          ? ['all']
          : [filterState.selectedExpiry],
    selectedSide: filterState.selectedSide,
    selectedMoneyness: filterState.selectedMoneyness,
    selectedMoneynessC: filterState.selectedMoneynessC,
    selectedDataMode: filterState.selectedDataMode,
    selectedSymbol: filterState.selectedSymbol || 'all',
    selectedStrategy: filterState.selectedStrategy || 'all',
    selectedTimeframe: filterState.selectedTimeframe || 'all',
    selectedVolume: filterState.selectedVolume || 'all',
    selectedDelta: filterState.selectedDelta || 'all',
    
    // Setters
    setSelectedUnderlying,
    setSelectedExpiry,
    setSelectedExpiries,
    setSelectedSide,
    setSelectedMoneyness,
    setSelectedMoneynessC,
    setSelectedDataMode,
    setSelectedSymbol,
    setSelectedStrategy,
    setSelectedTimeframe,
    setSelectedVolume,
    setSelectedDelta,
    
    // Utility functions
    resetFilters,
    clearStoredState,
    
    // Full state for advanced usage
    filterState,
    setFilterState
  };
};

// Global function to clear all component filter states (useful for admin/debugging)
export const clearAllFilterStates = () => {
  const keys = Object.keys(localStorage).filter(key => key.startsWith('filter-state-'));
  keys.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`⚠️ Failed to remove ${key}:`, error);
    }
  });
  console.log(`🗑️ Cleared ${keys.length} filter states`);
};

// Get all stored filter states (useful for debugging)
export const getAllFilterStates = (): Record<string, ComponentFilterState> => {
  const states: Record<string, ComponentFilterState> = {};
  const keys = Object.keys(localStorage).filter(key => key.startsWith('filter-state-'));
  
  keys.forEach(key => {
    try {
      const componentName = key.replace('filter-state-', '');
      const stored = localStorage.getItem(key);
      if (stored) {
        states[componentName] = JSON.parse(stored);
      }
    } catch (error) {
      console.warn(`⚠️ Failed to parse stored state for ${key}:`, error);
    }
  });
  
  return states;
};

// Debug function to log all current filter states
export const debugAllFilterStates = () => {
  const states = getAllFilterStates();
  console.group('🔍 All Persistent Filter States');
  Object.entries(states).forEach(([component, state]) => {
    console.log(`📊 ${component}:`, state);
  });
  console.groupEnd();
  return states;
};

// Function to export all filter states (useful for backup/restore)
export const exportFilterStates = (): string => {
  const states = getAllFilterStates();
  return JSON.stringify(states, null, 2);
};

// Function to import filter states (useful for backup/restore)
export const importFilterStates = (statesJson: string): void => {
  try {
    const states = JSON.parse(statesJson);
    Object.entries(states).forEach(([component, state]) => {
      localStorage.setItem(getStorageKey(component), JSON.stringify(state));
    });
    console.log(`📥 Imported filter states for ${Object.keys(states).length} components`);
  } catch (error) {
    console.error('❌ Failed to import filter states:', error);
  }
}; 